import { formatCompactINR, formatINR } from "../../lib/format";
import { TrendingUp, Flame, Award, Gauge } from "lucide-react";

function Highlight({ icon: Icon, tint, label, service, metric }) {
  if (!service) return null;
  return (
    <div className="rounded-xl bg-[#1E293B] border border-white/10 p-5" style={{ borderLeft: `3px solid ${tint}` }}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider" style={{ color: tint }}>
        <Icon className="w-4 h-4" /> {label}
      </div>
      <p className="font-display text-xl font-semibold mt-2 text-white">{service.name}</p>
      <p className="text-sm text-slate-400 mt-1">{metric}</p>
    </div>
  );
}

export default function ServiceRanking({ matrix }) {
  if (!matrix || !matrix.length) return null;
  const withSpend = matrix.filter((m) => m.spend > 0);
  const mostSpend = [...withSpend].sort((a, b) => b.spend - a.spend)[0];
  const bestRoas = [...withSpend].sort((a, b) => b.roas - a.roas)[0];
  const cheapestLead = [...withSpend].sort((a, b) => a.cpl - b.cpl)[0];
  const totalSpend = matrix.reduce((s, m) => s + m.spend, 0) || 1;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Highlight icon={Flame} tint="#EF4444" label="Highest Spend" service={mostSpend}
          metric={`${formatCompactINR(mostSpend?.spend)} · ${((mostSpend?.spend / totalSpend) * 100).toFixed(0)}% of budget`} />
        <Highlight icon={Award} tint="#10B981" label="Most Cost-Effective" service={bestRoas}
          metric={`${bestRoas?.roas}x ROAS · CPL ${formatINR(bestRoas?.cpl)}`} />
        <Highlight icon={Gauge} tint="#38BDF8" label="Cheapest Leads" service={cheapestLead}
          metric={`CPL ${formatINR(cheapestLead?.cpl)} vs target ${formatINR(cheapestLead?.target_cpl)}`} />
      </div>

      <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-[#38BDF8]" />
          <h2 className="font-display text-lg font-semibold">Efficiency Ranking</h2>
        </div>
        <div className="space-y-3">
          {[...matrix].sort((a, b) => b.roas - a.roas).map((s, i) => {
            const cplOver = s.cpl > s.target_cpl;
            const maxRoas = Math.max(...matrix.map((m) => m.roas), 1);
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="w-6 text-xs text-slate-500 tabular">#{i + 1}</span>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                <span className="text-sm text-slate-200 w-44 truncate">{s.name}</span>
                <div className="flex-1 h-2 rounded-full bg-[#0F172A] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(s.roas / maxRoas) * 100}%`, background: s.color }} />
                </div>
                <span className="text-sm font-medium text-[#10B981] w-14 text-right tabular">{s.roas}x</span>
                <span className="text-xs w-24 text-right tabular" style={{ color: cplOver ? "#EF4444" : "#94A3B8" }}>
                  CPL {formatINR(s.cpl)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
